import React from "react";
import {
  CreateMaintenanceAreaDto,
  MaintenanceAreaDto,
  maintenanceAreasApi,
} from "../../../services/api";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";

interface Props {
  area: MaintenanceAreaDto | null;
  onClose: () => void;
  onSaved: () => void;
}

export const AreaFormDialog: React.FC<Props> = ({ area, onClose, onSaved }) => {
  const isEdit = area != null;
  const [name, setName] = React.useState(area?.name ?? "");
  const [description, setDescription] = React.useState(area?.description ?? "");
  const [discordWebhookUrl, setDiscordWebhookUrl] = React.useState(area?.discordWebhookUrl ?? "");
  const [isActive, setIsActive] = React.useState(area?.isActive ?? true);
  const [saving, setSaving] = React.useState(false);

  const handleSave = async () => {
    if (!name.trim()) {
      window.alert("El nombre es obligatorio");
      return;
    }
    setSaving(true);
    try {
      const payload: CreateMaintenanceAreaDto = {
        name: name.trim(),
        description: description.trim() || null,
        discordWebhookUrl: discordWebhookUrl.trim() || null,
        isActive,
      };
      if (isEdit && area) {
        await maintenanceAreasApi.update(area.id, payload);
      } else {
        await maintenanceAreasApi.create(payload);
      }
      onSaved();
    } catch (e) {
      window.alert(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Editar área" : "Nueva área"}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label htmlFor="area-name">Nombre *</Label>
            <Input
              id="area-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Ej. Camioneros, Producción, Almacén"
            />
          </div>

          <div>
            <Label htmlFor="area-desc">Descripción</Label>
            <Textarea
              id="area-desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              placeholder="Para qué se usa esta área"
            />
          </div>

          <div>
            <Label htmlFor="area-webhook">Webhook de Discord</Label>
            <Input
              id="area-webhook"
              value={discordWebhookUrl}
              onChange={(e) => setDiscordWebhookUrl(e.target.value)}
              placeholder="https://discord.com/api/webhooks/…"
            />
            <p className="text-xs text-slate-500 mt-1">
              En Discord: <em>Ajustes del canal → Integraciones → Webhooks → Nuevo webhook → Copiar URL</em>.
              Si lo dejas vacío, los avisos se loguean pero no se envían.
            </p>
          </div>

          <div className="flex items-center gap-2">
            <Checkbox
              id="area-active"
              checked={isActive}
              onCheckedChange={(c) => setIsActive(c === true)}
            />
            <Label htmlFor="area-active">Activa</Label>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancelar</Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? "Guardando…" : isEdit ? "Guardar" : "Crear"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
